import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    FileAttachmentApiService,
    IncidentResponse,
    ReferenceBookResponse,
    RefundApiService,
    RefundsResponse,
    RefundStatus,
} from '@npck/af-frontend-api';
import {downloadFromBlobResponse, LocalizationService, NESTED_TAB, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {FilterField} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {ButtonColumn, Column, ColumnType, Row, StatusColumn} from '../../../../components/full-table/table/table.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {IncidentDetailsFilterHolderService} from '../../../../domain/incident-details-filter-holder.service';
import {INCIDENT_ID, REFUND_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {FINAL_REFUND_STATUSES, getRefundStatusInfo} from '../../../refunds/refunds.component';
import {IncidentsTab} from '../../incidents.component';
import {IncidentDetailsTab} from '../incident-details.component';

@Component({
    selector: 'af-refunds-tab',
    templateUrl: './refunds-tab.component.html',
    styleUrl: './refunds-tab.component.scss',
    standalone: false,
})
export class RefundsTabComponent implements OnInit {
    @Input() incident: IncidentResponse;
    @Output() createRefundListener = new EventEmitter<void>();
    hasCreateButton: boolean = false;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    response: RefundsResponse;
    private backToTab: IncidentsTab;

    constructor(
        private activatedRoute: ActivatedRoute,
        private refundsApiService: RefundApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private localizationService: LocalizationService,
        private fileAttachmentApiService: FileAttachmentApiService,
        private filterHolderService: IncidentDetailsFilterHolderService,
    ) {
        this.backToTab = this.activatedRoute.snapshot.parent.queryParamMap.get(TAB) as IncidentsTab;
    }

    ngOnInit(): void {
        this.loadRefunds(
            this.filterHolderService.getState(this.incident.id, 'tabRefunds')?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadRefunds(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.incident.id, 'tabRefunds')?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.incident.id, 'tabRefunds', this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference('bin'),
            this.dictionaryHolderService.getReference('refund_type'),
            this.dictionaryHolderService.getReference('refund_status'),
            this.refundsApiService.getRefunds({
                incidentId: this.incident.id,
                id: this.tableEvent.filter.query,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortBy: this.tableEvent.sortBy?.columnName,
                sortOrder: this.tableEvent.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([organizations, refundTypes, refundStatuses, response]) => {
                this.buildDataSource(response, refundTypes, refundStatuses, organizations);
                const isMyOrganizationRelatedToIncident = [
                    ...this.incident.related,
                    this.incident.governmentBin,
                    this.incident.source_organization
                ]
                    .map(name => findValueInDictionary(organizations, name))
                    .filter(code => this.authHolderService.organizationBin === code)
                    .length > 0;
                this.hasCreateButton = !this.incident.isDeleted && isMyOrganizationRelatedToIncident
                    && this.incident.resolutionStatusCode !== 'NF';
            });
    }

    navigateToRefunds(): void {
        this.routerService.navigate(Path.REFUNDS, {queryParams: {[INCIDENT_ID]: this.incident.id}});
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            filter: {
                query: '',
            },
        };
    }

    private buildDataSource(
        response: RefundsResponse,
        refundTypes: ReferenceBookResponse[],
        refundStatuses: ReferenceBookResponse[],
        organizations: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.refunds.map(item => {
            let columnButton: Column;
            let buttonLabel = '';
            const paymentOrder = item.attachments.find(attachment => attachment.docType === 'PAYMENT_ORDER')?.files?.[0];
            if (paymentOrder && item.status === RefundStatus.EXECUTED) {
                buttonLabel = 'i18n_af_buttonDownloadPaymentOrder';
            } else if (item.processingBin === this.authHolderService.organizationBin  && !FINAL_REFUND_STATUSES.includes(item.status)) {
                buttonLabel = 'i18n_af_button_processRefund';
            }
            if (buttonLabel) {
                columnButton = <ButtonColumn>{
                    type: ColumnType.BUTTON,
                    buttonType: 'secondary',
                    icon: 'ic_open',
                    buttonSize: 'extra-small',
                    text: buttonLabel,
                    click: () => {
                        if (item.status === RefundStatus.EXECUTED) {
                            this.downloadPaymentOrder(paymentOrder.id);
                        } else {
                            this.processRefund(item.id);
                        }
                    },
                };
            } else {
                columnButton = <Column>{
                    type: ColumnType.HIDDEN,
                };
            }
            const refundStatusInfo = getRefundStatusInfo(
                refundStatuses,
                item,
                this.localizationService,
            );
            return <Row>{
                rowStyle: item.status === RefundStatus.REJECTED ? 'error-light' : '',
                click: () => this.routerService.navigate(Path.INCIDENTS_REFUND_DETAILS, {
                        pathParams: {
                            [INCIDENT_ID]: item.incidentId,
                            [REFUND_ID]: item.id,
                        },
                        queryParams: {
                            [TAB]: this.backToTab,
                            [NESTED_TAB]: 'tabRefunds' as IncidentDetailsTab,
                        },
                    },
                ),
                created_date: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.created_date,
                },
                status: <StatusColumn>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: refundStatusInfo.text,
                    style: refundStatusInfo.style,
                },
                bankSender: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.senderBin)?.name_ru ?? '-',
                },
                bankReceiver: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.receiverBin)?.name_ru ?? '-',
                },
                refundType: <Column>{
                    sortable: true,
                    type: ColumnType.TEXT,
                    text: refundTypes.find(status => status.code === item.type)?.name_ru ?? '-',
                },
                processingBin: <Column>{
                    type: ColumnType.TEXT,
                    text: organizations.find(dictionary => dictionary.code === item.processingBin)?.name_ru ?? '-',
                },
                columnButton: columnButton,
                id: <Column>{
                    type: ColumnType.TEXT,
                    text: item.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
            };
        });
    }

    private processRefund(id: string): void {
        this.routerService.navigate(Path.INCIDENTS_REFUND_DETAILS, {
            pathParams: {
                [INCIDENT_ID]: this.incident.id,
                [REFUND_ID]: id,
            },
        });
    }

    private downloadPaymentOrder(fileId: string): void {
        this.fileAttachmentApiService.downloadFile(fileId, 'response')
            .pipe(rxSpinner(`downloadFile_${fileId}`))
            .subscribe(response => downloadFromBlobResponse(response));
    }
}
